import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { InputDirective } from '../../spartan/input.directive';
import {
  COMBOBOX_DEBOUNCE_MS,
  COMBOBOX_EMPTY_LABEL,
  COMBOBOX_LOADING_LABEL,
  COMBOBOX_REFINE_HINT,
  ComboboxOption,
} from './searchable-combobox.model';

type ComboboxEntry = ComboboxOption | null;

@Component({
  selector: 'app-searchable-combobox',
  standalone: true,
  imports: [InputDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'relative block', '(focusout)': 'onFocusOut($event)' },
  templateUrl: './searchable-combobox.component.html',
})
export class SearchableComboboxComponent {
  readonly inputId = input.required<string>();
  readonly allLabel = input.required<string>();
  readonly options = input<readonly ComboboxOption[]>([]);
  readonly selected = input<ComboboxOption | null>(null);
  readonly loading = input(false);
  readonly hasMore = input(false);
  readonly disabled = input(false);
  readonly maxLength = input<number | null>(null);
  readonly debounceMs = input(COMBOBOX_DEBOUNCE_MS);
  readonly searchChange = output<string>();
  readonly selectionChange = output<ComboboxOption | null>();

  protected readonly refineHint = COMBOBOX_REFINE_HINT;
  protected readonly loadingLabel = COMBOBOX_LOADING_LABEL;
  protected readonly emptyLabel = COMBOBOX_EMPTY_LABEL;

  protected readonly open = signal(false);
  protected readonly query = signal('');
  protected readonly activeIndex = signal(0);

  protected readonly listId = computed(() => `${this.inputId()}-listbox`);
  protected readonly entries = computed<readonly ComboboxEntry[]>(() => [null, ...this.options()]);
  protected readonly displayValue = computed(() =>
    this.open() ? this.query() : (this.selected()?.label ?? ''),
  );
  protected readonly placeholder = computed(() => this.selected()?.label ?? this.allLabel());

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private debounceHandle: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.cancelPendingSearch());
    effect(() => {
      if (this.disabled()) untracked(() => this.close());
    });
  }

  protected optionId(index: number): string {
    return `${this.inputId()}-option-${index}`;
  }

  protected entryLabel(entry: ComboboxEntry): string {
    return entry?.label ?? this.allLabel();
  }

  protected isSelected(entry: ComboboxEntry): boolean {
    const selected = this.selected();
    return entry === null ? selected === null : selected?.key === entry.key;
  }

  protected onFocus(): void {
    if (this.disabled() || this.open()) return;
    this.open.set(true);
    this.query.set('');
    this.activeIndex.set(0);
    this.emitSearch('');
  }

  protected onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.open.set(true);
    this.query.set(value);
    this.activeIndex.set(0);
    this.scheduleSearch(value.trim());
  }

  protected onKeydown(event: KeyboardEvent): void {
    const last = this.entries().length - 1;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!this.open()) this.onFocus();
      else this.activeIndex.update((index) => Math.min(index + 1, last));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex.update((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && this.open()) {
      event.preventDefault();
      this.choose(this.entries()[this.activeIndex()] ?? null);
    } else if (event.key === 'Escape' && this.open()) {
      event.preventDefault();
      this.close();
    }
  }

  protected onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (next && this.host.nativeElement.contains(next)) return;
    this.close();
  }

  protected choose(entry: ComboboxEntry): void {
    this.selectionChange.emit(entry);
    this.close();
  }

  private close(): void {
    this.cancelPendingSearch();
    this.open.set(false);
    this.query.set('');
  }

  private scheduleSearch(term: string): void {
    this.cancelPendingSearch();
    this.debounceHandle = setTimeout(() => this.searchChange.emit(term), this.debounceMs());
  }

  private emitSearch(term: string): void {
    this.cancelPendingSearch();
    this.searchChange.emit(term);
  }

  private cancelPendingSearch(): void {
    if (this.debounceHandle !== undefined) clearTimeout(this.debounceHandle);
    this.debounceHandle = undefined;
  }
}
